// ユーザーロール
export const userRole = {
  // 運営スタッフ（10-19）
  admin: 10, // 運営管理者（フル権限）
  operator: 11, // 運営オペレーター（閲覧・限定編集）

  // 企業ユーザー（1-9）
  companyAdmin: 1, // 企業管理者
  companyMember: 2, // 一般メンバー
};

// ユーザー登録ステータス
export const registerStatus = {
  finished: 1,
  emailRegistered: 2,
  deleted: 99,
};

// inputのステータス
export const inputStatus = {
  valid: 1, // 有効
  deleted: 99, // 削除
};

// 結果のステータス
export const resultStatus = {
  valid: 1, // 有効
  deleted: 99, // 削除
};

// 読取パターンのステータス
export const patternStatus = {
  valid: 1, // 有効
  deleted: 99, // 削除
};

// グループのステータス（#2804）
export const groupStatus = {
  valid: 1, // 有効
  deleted: 99, // 論理削除
};

// マスタのステータス（#2896）
export const masterStatus = {
  valid: 1, // 有効
  deleted: 99, // 論理削除
};

// マスタ照合管理画面のタブ（#3180）
export const masterPageTab = {
  masterList: 'master', // マスタ一覧タブ（既定）。マスタCSVの管理
  patternMapping: 'pattern', // 「パターンごとの照合設定」タブ。パターンごとの照合ステップを設定する
} as const;

export type MasterPageTab = (typeof masterPageTab)[keyof typeof masterPageTab];

// マスタ照合管理画面のタブの表示ラベル
export const masterPageTabLabel: Record<MasterPageTab, string> = {
  [masterPageTab.masterList]: 'マスタ一覧',
  [masterPageTab.patternMapping]: 'パターンごとの照合設定',
};

// 絞り込みキーの照合方式（#3180）
// 絞り込みは候補を狭めるだけでスコアリングしないため、ai（ベクトル検索）は選べない。
export const filterMatchMode = {
  exact: 'exact', // 完全一致（raw_data->>'列' = 値）。IDコード同士の照合に使う
  keyword: 'keyword', // 部分一致（raw_data->>'列' ILIKE %値%）
} as const;

// 検索キーの照合方式（#3022 で導入した match_mode を定数化。#3180）
export const searchMatchMode = {
  ai: 'ai', // AI照合（embedding + trigram のハイブリッド）
  keyword: 'keyword', // 部分一致（ILIKE）
} as const;

// マスタ照合の閾値（#3263）。画面表示・保存はすべて 0〜100 の整数（%）で扱う。
// 内部の類似度は 0〜1 のため、バックエンド側で 100 で割って使う想定。
// 対象は OCR直後の自動照合のみ（手動の再照合は候補を人が選ぶため絞り込まない）。
export const masterMatchThreshold = {
  // 入力可能な範囲。0% は「常に自動反映」、100% は「完全一致のみ自動反映」を意味する。
  min: 0,
  max: 100,
  // 照合設定フォームの初期値。未設定のマッピングを編集で開いたときもこの値を表示する。
  // 保存されていないマッピングに実際に適用される既定値はバックエンドの定数が正のため、
  // 値を変えるときは必ずバックエンド側と揃えること。
  defaultAutoApplyScore: 80,
  // 要注意にする下限スコアの初期値（#3309）。マッピングに未設定なら
  // functions の MASTER_MATCH_REVIEW_THRESHOLD が使われるため、値を変えるときは
  // 必ずバックエンド側と揃えること。
  defaultReviewThreshold: 90,
  // AI照合リランク（#3355）の初期値。マッピングに未設定なら OFF として扱う。
  // バックエンドも未設定は false 扱いのため、値を変えるときは必ず揃えること。
  defaultEnableLlmRerank: false,
} as const;

// パターン/OCR結果の共有範囲（#2804 Phase2/3）
export const shareScope = {
  company: 'company', // 企業全体（既定・後方互換）
  group: 'group', // グループ限定
  private: 'private', // 作成者のみ
} as const;

// グループ機能の上限値（#2804）
export const groupLimit = {
  maxGroupsPerCompany: 50, // 1企業あたりの最大グループ数
  maxGroupsPerUser: 10, // 1ユーザーの最大所属グループ数
  groupNameMaxLength: 50, // グループ名の最大文字数
  groupDescriptionMaxLength: 500, // 説明の最大文字数
} as const;

// 管理処理ステータス
export const processingStatus = {
  draft: 0,
  processing: 1,
  completed: 2,
  outputted: 3,
};

// 管理処理ステータスの表示ラベル
export const processingStatusLabel = {
  [processingStatus.processing]: '未処理',
  [processingStatus.completed]: '確認済',
  [processingStatus.outputted]: '出力済',
} as const;

// アップロード状況ステータス
export const ocrStatus = {
  uploaded: 1, // アップロード完了
  processing: 2, // OCR処理中
  completed: 3, // OCR処理完了
  error: 99, // エラー
};

// マスタCSV取り込みバッチのステータス
// Cloud SQL csv_job_histories.status (running/succeeded/failed) を数値に正規化したもの
export const batchStatus = {
  processing: 2, // 処理中 (running)
  completed: 3, // 完了 (succeeded)
  error: 99, // 失敗 (failed または 30 分以上 running 残留)
};

export const demoStatusEnum = {
  success: 1, // 完了
  error: 99, // 失敗
};

// Firestore の writeBatch 1 コミットあたりの最大オペレーション数（SDK 制約）
export const firestoreBatchLimit = 500;

// 読取パターン作成・編集画面のステップ（PatternAdd.tsx の Stepper と対応）
export const patternStep = {
  upload: 0, // アップロード
  itemSetting: 1, // 項目設定
  settingSave: 2, // 設定・保存
} as const;

// ファイル処理タイプ
export const fileProcessingType = {
  pagePerResult: 1, // 1ページ→1つの結果（ページごとに分割）
  singleResult: 2, // 1ファイル→1つの結果（複数ページも統合）
};

// OCRエンジン（#2960 精度検証用・運営限定で切替）
// Gemini への入力方式を切り替える。未指定時は visionAi（従来動作）。
export const ocrEngineType = {
  visionAi: 0, // Azure Vision の行テキスト + 画像（現行デフォルト）
  documentIntelligence: 1, // Azure DI の行テキスト + 画像
  documentIntelligenceWithTables: 2, // DI の行テキスト + 表構造 + 画像（表精度の本命）
  geminiOnly: 3, // 画像のみ（OCRテキストなし・Azureコストゼロ）
};

// OCRエンジン選択のUI選択肢（#2960 運営限定）。
// 「エンジン × 座標付与」を1つのプルダウンにまとめる。選択値は id、内部では
// ocrEngine（ocrEngineType）と includeCoordinates（座標を渡すか）に展開して送信する。
// 内部の送信・BE は従来どおり（ocrEngine + includeCoordinates を別々に受け取る）。
export const ocrEngineOption = [
  { id: 0, label: 'Vision AI', ocrEngine: ocrEngineType.visionAi, includeCoordinates: false },
  {
    id: 1,
    label: 'Vision AI + 座標渡す',
    ocrEngine: ocrEngineType.visionAi,
    includeCoordinates: true,
  },
  {
    id: 2,
    label: 'Document Intelligence',
    ocrEngine: ocrEngineType.documentIntelligence,
    includeCoordinates: false,
  },
  {
    id: 3,
    label: 'Document Intelligence + 座標渡す',
    ocrEngine: ocrEngineType.documentIntelligence,
    includeCoordinates: true,
  },
  {
    id: 4,
    label: 'Document Intelligence + 表構造渡す',
    ocrEngine: ocrEngineType.documentIntelligenceWithTables,
    includeCoordinates: false,
  },
  {
    id: 5,
    label: 'Document Intelligence + 表構造 + 座標渡す',
    ocrEngine: ocrEngineType.documentIntelligenceWithTables,
    includeCoordinates: true,
  },
  {
    id: 6,
    label: 'OCRエンジンなし（画像のみ）',
    ocrEngine: ocrEngineType.geminiOnly,
    includeCoordinates: false,
  },
] as const;

// OCRエンジンの選択肢（#3264）。
//
// 上の ocrEngineOption は運営の検証用で、座標付与の有無まで含めた8通りを並べている。
// こちらは「どの帳票にどれを使うか」を選ぶためのもの。
// ユーザーへの課金は OCR エンジンでは変わらない（原価のみ変動）ため、費用は表示しない。

// パターン設定画面（運営スタッフのみ）用。
// 運営は検証結果と突き合わせるため、帳票の特徴に加えてエンジン名も出す
export const ocrEngineChoiceForOperator = [
  {
    label: '標準（手書き・一般的な帳票）- Azure AI Vision',
    value: ocrEngineType.visionAi,
  },
  {
    label: '表形式の帳票 - Document Intelligence',
    value: ocrEngineType.documentIntelligence,
  },
  {
    label: '複雑な表でデータ量が多い帳票 - Document Intelligence + 表構造',
    value: ocrEngineType.documentIntelligenceWithTables,
  },
] as const;

// アップロード画面（一般ユーザー）用。
// ユーザーはエンジン名を知る必要がないため、帳票の特徴だけで見せる
export const ocrEngineChoice = [
  { label: '標準（手書き・一般的な帳票）', value: ocrEngineType.visionAi },
  { label: '表形式の帳票', value: ocrEngineType.documentIntelligence },
  { label: '複雑な表でデータ量が多い帳票', value: ocrEngineType.documentIntelligenceWithTables },
] as const;

// (ocrEngine, includeCoordinates) から、対応する ocrEngineOption の id を逆引きする。
// プルダウンの選択値（現在値）を復元するために使う。該当なしは 0（Vision AI）。
export function getOcrEngineOptionId(ocrEngine: number, includeCoordinates: boolean): number {
  const found = ocrEngineOption.find(
    o => o.ocrEngine === ocrEngine && o.includeCoordinates === includeCoordinates,
  );
  return found ? found.id : 0;
}

// 2回目の構造化エンジン（#2960 精度検証用・運営限定で切替）。
// 未指定時は gemini（#3375 以降は Gemini 3.1 Flash-Lite）。
export const structuredEngineType = {
  gemini: 0, // 構造化出力の既定（#3375 で Gemini 3.1 Flash-Lite に変更）
  chatgpt: 1, // ChatGPT（Azure OpenAI GPT-4o）Structured Outputs
  geminiFlashLite31: 2, // Gemini 3.1 Flash-Lite 構造化出力
  geminiFlashLite35: 3, // Gemini 3.5 Flash-Lite 構造化出力
};

// Geminiモデル
// ※ enum値は Firestore に保存済みのため、廃止モデルでも値は再利用・削除しない。
export const geminiModel = {
  flash_2_5: 1, // gemini-2.5-flash（廃止予定。#3375 以降は実行時に flash_lite_3_5 へ振り替える）
  pro_2_5: 2, // gemini-2.5-pro
  pro_3: 3, // gemini-3-pro-preview
  flash_3: 4, // gemini-3-flash-preview
  flash_lite_2_5: 5, // 提供終了（2026/10/16）。過去の結果の表示用に値は残す。選択肢には出さない
  flash_3_5: 6, // gemini-3.5-flash
  flash_lite_3_1: 7, // gemini-3.1-flash-lite (低コスト)
  flash_3_6: 8, // gemini-3.6-flash（廃止済み・#3375 で選択肢から削除。過去結果の表示用に値のみ残す）
  flash_lite_3_5: 9, // gemini-3.5-flash-lite (低コスト)
};

// 値取得（1回目OCR）で使う既定モデル（#3375）。
// デフォルトを差し替えるときはこの定数だけを変更する。
export const GEMINI_MODEL_DEFAULT = geminiModel.flash_lite_3_5;

// 提供終了（予定）のモデルを、実行時に振り替わる後継モデルへ読み替える（#3245・#3375）。
// 保存済みのパターン・結果の値をそのまま選択状態に復元すると、選択肢に無くて空欄になったり、
// 表示と実際に動くモデルが食い違ったりする。選択状態を復元するときに通す。
// Functions 側の getGeminiModelName も同じ振り替えをしている
export const toAvailableGeminiModel = (modelValue: number): number => {
  switch (modelValue) {
    case geminiModel.flash_2_5:
      return GEMINI_MODEL_DEFAULT;
    case geminiModel.flash_lite_2_5:
      return geminiModel.flash_lite_3_1;
    default:
      return modelValue;
  }
};

// Pro 系のモデルか（#3206）。
// 「パターンがモデル選択可能か」「その結果を Pro で読み取ったか」の判定に使う。
// 判定を1か所にまとめ、Pro モデルが増えたときの追従漏れを防ぐ。
export const isProGeminiModel = (modelValue?: number): boolean =>
  modelValue === geminiModel.pro_2_5 || modelValue === geminiModel.pro_3;

// 各Geminiモデルの入力/出力 単価（USD per 1M tokens）。
// モデル選択プルダウンの2段目に「入力/出力の値段」を表示するために使う。
// 数値は functions/src/constants/pricing.ts の定義と一致させること。
export const geminiModelPricePer1M = {
  [geminiModel.flash_2_5]: { input: 0.3, output: 2.5 },
  [geminiModel.pro_2_5]: { input: 1.25, output: 10.0 },
  [geminiModel.pro_3]: { input: 2.0, output: 12.0 },
  [geminiModel.flash_3]: { input: 0.5, output: 3.0 },
  [geminiModel.flash_lite_2_5]: { input: 0.1, output: 0.4 },
  [geminiModel.flash_3_5]: { input: 1.5, output: 9.0 },
  [geminiModel.flash_lite_3_1]: { input: 0.25, output: 1.5 },
  [geminiModel.flash_lite_3_5]: { input: 0.3, output: 2.5 },
} as const;

// モデル選択プルダウンの2段目に出す単価ラベル（例: "入力 $0.30 / 出力 $2.50（per 1M）"）。
export function getGeminiModelPriceLabel(modelValue: number): string {
  const p = geminiModelPricePer1M[modelValue as keyof typeof geminiModelPricePer1M];
  if (!p) return '';
  return `入力 $${p.input.toFixed(2)} / 出力 $${p.output.toFixed(2)}（per 1M）`;
}

// GPT-4o の単価ラベル（構造化エンジン選択の2段目用）。
export const GPT4O_PRICE_LABEL = '入力 $2.50 / 出力 $10.00（per 1M）';

// Gemini 2.5用 思考バジェット設定（thinkingBudget: 数値）
export const geminiThinkingBudget = {
  auto: -1, // 自動（モデルが決定）- デフォルト
  disabled: 0, // 思考無効
  minimal: 1024, // 最小限（1024トークン）
  low: 4096, // 低（4096トークン）
  medium: 8192, // 中（8192トークン）
  high: 16384, // 高（16384トークン）
  max: 24576, // 最大（24576トークン）
};

// Gemini 3 Flash用 思考レベル設定（thinkingLevel: 文字列）
export const geminiThinkingLevel = {
  minimal: 'minimal', // ほとんど思考なし
  low: 'low', // 低
  medium: 'medium', // 中（デフォルト）
  high: 'high', // 高
};

// Gemini 3 Pro用 思考レベル設定（thinkingLevel: 文字列）
export const geminiThinkingLevelPro = {
  low: 'low', // 低
  high: 'high', // 高（デフォルト）
};

// 思考量スライダー（#2960）。0=無効/未設定 〜 4=高 の5段で全モデルを統一表示する。
// モデルによって思考の指定方式（budget数値 / level文字列）が違うため、
// スライダー値をモデルに応じて thinkingBudget / thinkingLevel に変換する。
export const geminiThinkingSlider = {
  min: 0,
  max: 4,
  default: 0, // 未選択は0（無効・従来デフォルト）
  labels: ['無効', '最小', '低', '中', '高'] as const,
};

// モデルの思考指定方式。'none'=思考非対応, 'budget'=2.5系(数値), 'level'=3系Flash(文字列), 'levelPro'=3系Pro(low/highのみ)
export type GeminiThinkingType = 'none' | 'budget' | 'level' | 'levelPro';
export function getGeminiThinkingType(modelValue: number): GeminiThinkingType {
  switch (modelValue) {
    case geminiModel.flash_2_5:
    case geminiModel.pro_2_5:
      return 'budget';
    case geminiModel.flash_3:
    case geminiModel.flash_3_5:
      return 'level';
    case geminiModel.pro_3:
      return 'levelPro';
    // Flash-Lite 系は思考非対応
    case geminiModel.flash_lite_2_5:
    case geminiModel.flash_lite_3_1:
    case geminiModel.flash_lite_3_5:
    default:
      return 'none';
  }
}

// スライダー値(0〜4) → thinkingBudget(数値) / thinkingLevel(文字列) に変換する。
// 送信直前に呼び、モデルに応じた片方だけを設定する（他方は null）。
export function sliderValueToThinking(
  modelValue: number,
  sliderValue: number,
): { thinkingBudget: number | null; thinkingLevel: string | null } {
  const type = getGeminiThinkingType(modelValue);
  // budget型（2.5系）: 段階をトークン数に対応
  if (type === 'budget') {
    const budgetByStep = [
      geminiThinkingBudget.disabled, // 0 無効
      geminiThinkingBudget.minimal, // 1 最小
      geminiThinkingBudget.low, // 2 低
      geminiThinkingBudget.medium, // 3 中
      geminiThinkingBudget.high, // 4 高
    ];
    return {
      thinkingBudget: budgetByStep[sliderValue] ?? geminiThinkingBudget.disabled,
      thinkingLevel: null,
    };
  }
  // level型（3系Flash）: 0は未設定(null)、1〜4を minimal/low/medium/high に
  if (type === 'level') {
    const levelByStep: (string | null)[] = [
      null, // 0 未設定
      geminiThinkingLevel.minimal, // 1
      geminiThinkingLevel.low, // 2
      geminiThinkingLevel.medium, // 3
      geminiThinkingLevel.high, // 4
    ];
    return { thinkingBudget: null, thinkingLevel: levelByStep[sliderValue] ?? null };
  }
  // levelPro型（3系Pro）: low/high のみ。0=未設定, 1〜2=low, 3〜4=high
  if (type === 'levelPro') {
    const level =
      sliderValue <= 0
        ? null
        : sliderValue <= 2
          ? geminiThinkingLevelPro.low
          : geminiThinkingLevelPro.high;
    return { thinkingBudget: null, thinkingLevel: level };
  }
  // none（思考非対応）: どちらも設定しない
  return { thinkingBudget: null, thinkingLevel: null };
}

// 現在の thinkingBudget / thinkingLevel からスライダー位置(0〜4)を逆算する。
// スライダーの表示値を復元するために使う。判別できない値は 0 とみなす。
export function thinkingToSliderValue(
  modelValue: number,
  thinkingBudget: number | null,
  thinkingLevel: string | null,
): number {
  const type = getGeminiThinkingType(modelValue);
  if (type === 'budget') {
    switch (thinkingBudget) {
      case geminiThinkingBudget.minimal:
        return 1;
      case geminiThinkingBudget.low:
        return 2;
      case geminiThinkingBudget.medium:
        return 3;
      case geminiThinkingBudget.high:
      case geminiThinkingBudget.max:
        return 4;
      default:
        return 0; // disabled / auto / null
    }
  }
  if (type === 'level' || type === 'levelPro') {
    switch (thinkingLevel) {
      case geminiThinkingLevel.minimal:
        return 1;
      case geminiThinkingLevel.low:
        return 2;
      case geminiThinkingLevel.medium:
        return 3;
      case geminiThinkingLevel.high:
        return 4;
      default:
        return 0;
    }
  }
  return 0;
}

// Gemini サンプリングパラメータ（#2960 運営スタッフ限定の精度検証用）
// 1回目OCR抽出の generationConfig に渡す。UIのスライダー範囲・初期値に使う。
export const geminiSamplingParam = {
  // temperature: 0.0〜2.0。低いほど決定的（表の読み取りは低温が有利な想定）
  temperatureMin: 0,
  temperatureMax: 2,
  temperatureDefault: 0,
  temperatureStep: 0.1,
  // topP: 0.0〜1.0。累積確率のしきい値（核サンプリング）
  // ※ topK は gemini-2.5系では固定値64で変更が効かない（実測確認済み）ため設けない
  topPMin: 0,
  topPMax: 1,
  topPDefault: 0, // デフォルトは最小（#2960 検証の初期値を最も決定的な側に寄せる）
  topPStep: 0.05,
};

// 企業契約プラン
export const companyPlan = {
  free: 0, // フリー
  standard: 1, // スタンダード
  pro: 2, // プロ
  custom: 3, // オーダーメード
};

// 企業ステータス
export const companyStatus = {
  invited: 0, // 招待中
  active: 1, // 有効
  suspended: 9, // 利用停止 (#2802 で 2→9 に変更)
  deleted: 99, // 削除
};

// 表示幅
export const displayWidth = {
  large: 1, // 大
  medium: 2, // 中
  small: 3, // 小
};

// 支払いサイクル
export const paymentCycle = {
  none: 0, // 支払いなし（無料）
  monthly: 1, // 月払い
  yearly: 2, // 年払い
};

// 支払い方法
export const paymentMethod = {
  invoice: 1, // 請求書払い
  creditCard: 2, // クレカ払い
};

// 担当者数の上限
export const userLimitMax = 300;

// 読取パターン数の上限
export const patternLimitMax = 300;

// AIマスタ照合レコード数の上限（事務局が企業ごとに設定できる値の最大）
export const masterRecordLimitMax = 1000000;

/**
 * AIマスタ照合レコード数上限のデフォルト値（#3336）。
 * レコード数に応じて月額料金が変わるため、事務局がマスタ機能を有効化した時点で
 * 未設定の企業に自動設定する。事務局は後から個別に変更できる。
 */
export const masterRecordLimitDefault = 10000;

// 上記デフォルト値を自動設定した際に master_record_limit_history に残す備考（#3336）
export const masterRecordLimitAutoDefaultNote =
  'マスタ機能の有効化にともない、デフォルト値を自動設定';

// プラン別の制限値
export const planLimits = {
  free: {
    userLimit: 1, // ユーザー数上限
    ocrMonthlyBudget: 250, // 月間OCR枠（円）
    patternLimit: 1, // 読取パターン数上限
    defaultUsageLimit: 5, // OCR利用枚数上限のデフォルト値（250円÷50円/枚）
  },
  standard: {
    userLimit: 10,
    ocrMonthlyBudget: 5000,
    patternLimit: 100,
    defaultUsageLimit: 100, // OCR利用枚数上限のデフォルト値
  },
  pro: {
    userLimit: 10,
    ocrMonthlyBudget: 5000,
    patternLimit: 100,
    defaultUsageLimit: 100,
  },
  custom: {
    userLimit: 30,
    ocrMonthlyBudget: 13500,
    patternLimit: 100,
    defaultUsageLimit: 100,
  },
};

// 企業登録時の初期値
export const companyInitialValues = {
  currentUserCount: 1, // 現在の担当者数（登録者本人）
  currentPatternCount: 0, // 現在の読取パターン数
  ocrCreditBalance: 0, // OCRクレジット残高（円）
  additionalUserTickets: 0, // 追加担当者枠
  allowAiLearning: true, // AI学習許可フラグ
};

// ファイル出力処理ステータス
export const fileExportProcessingStatus = {
  processing: 1, // 出力中
  completed: 2, // 完了
  error: 99, // エラー
};

// ファイル出力ステータス
export const fileExportStatus = {
  active: 1, // 有効
  deleted: 99, // 削除
};

// お問い合わせカテゴリ
export const contactCategory = {
  account: 1, // アカウントに関すること
  plan: 2, // 料金プランに関すること
  system: 3, // システムに関すること
  other: 99, // その他
};

// OCRクレジット使用種別
export const usageType = {
  ocrUsage: 1, // OCR処理による消費
  purchase: 2, // ユーザーによる購入
  manualGrant: 3, // 運営による手動追加
  adjustment: 4, // 調整
  expiration: 5, // 有効期限切れによる失効
  referral: 6, // リファラル特典
  coupon: 7, // クーポン特典
};

// FIFO消費対象の付与種別
export const creditGrantTypes = [
  usageType.purchase,
  usageType.manualGrant,
  usageType.referral,
  usageType.coupon,
];

// プラン別クレジット消費レート（円/枚）
export const planCreditRate = {
  free: 50, // 50円/枚
  standard: 50, // 50円/枚
  pro: 15, // 15円/枚
  custom: 15, // 15円/枚
};

// OCR実行種別（ocr_executionsサブコレクション用）
export const ocrExecutionType = {
  initial: 'initial', // 初回読み取り
  reOcrSingle: 're_ocr_single', // 特定項目の再読み取り
  reOcrAll: 're_ocr_all', // 全項目の再読み取り
  reclassification: 'reclassification', // 仕分け変更による再読み取り
} as const;

// 「その他」パターンID（該当パターンなし）
export const PATTERN_ID_OTHER = 'other';
export const FREE_OCR_PATTERN_ID = 'free_ocr';

// メール取り込み（#3042）: 専用アドレスの名称（論理名）の最大文字数。BE の EMAIL_INBOX_NAME_MAX_LENGTH と一致させる
export const EMAIL_INBOX_NAME_MAX_LENGTH = 50;

// 読取パターン作成・編集（STEP1）のサンプルアップロード上限。合計3ページ（4ページ目以降は自動で切り落とす。#2961）
export const PATTERN_SAMPLE_MAX_PAGES = 3;

// ===== 通常OCR（画面アップロード）の取り込み制限 =====
// メール取り込み経路（BE: functions/src/constants/ocrUpload.ts）とも値を揃える。変更時は両方を更新する。
// 1回のアップロードのファイル数上限（構造上限）。フリープラン+OCRクレジット無しは planLimits.free.defaultUsageLimit(5) が適用される
export const OCR_UPLOAD_MAX_FILES = 30;
// 1回のアップロードの合計ページ数上限（サーバー負荷軽減のため）
export const OCR_UPLOAD_MAX_TOTAL_PAGES = 200;
// singleResult（1ファイル=1結果）の場合の1PDFあたりのページ数上限
export const OCR_UPLOAD_MAX_PAGES_PER_FILE = 20;
// 1ファイルあたりの最大サイズ（10MB）
export const OCR_UPLOAD_FILE_MAX_BYTES = 10 * 1024 * 1024;

export type OcrExecutionType = (typeof ocrExecutionType)[keyof typeof ocrExecutionType];

/**
 * 比較の基準を決めるために読む OCR 実行ログの件数上限（#3318）。
 * 1件あたり result_data 全量を持つため全件取得は重い。
 * 基準は「最新の全項目実行 + それ以降の項目のみ読み取り」で足りる。
 */
export const OCR_EXECUTION_FETCH_LIMIT = 20;

/**
 * 子パターンの提案で「誤読」とみなす差の大きさ（#3380）。
 *
 * 違う文字数が「長いほうの文字数 × 比率（切り捨て、最低1文字）」以下なら誤読として扱い、
 * 提案の対象から外す。末尾の0の欠けや5桁中1桁の違いは、読む場所は合っていて
 * 罫線との重なりや手書きで読み違えただけなので、子パターンで場所を指定しても直らない。
 */
export const SUB_PATTERN_MISREAD_DIFF_RATIO = 0.2;
// これ未満の文字数の値は誤読とみなさない。1文字の値が違えば別の値を拾っている
export const SUB_PATTERN_MISREAD_MIN_LENGTH = 2;
// 行数を直した明細について、直す前に読み取っていた行をAIに見せる件数（#3380）
export const SUB_PATTERN_ROW_SAMPLE_COUNT = 3;

// OCR使用量種別（ocr_usage_monthly用）
export const ocrUsageType = {
  initial: 'initial', // 通常読み取り
  reOcr: 're_ocr', // 再読み取り
} as const;

export type OcrUsageType = (typeof ocrUsageType)[keyof typeof ocrUsageType];

// ファイル出力形式
export const exportFileType = {
  excel: 'excel',
  csv: 'csv',
} as const;

// CSV文字コード
export const exportEncoding = {
  utf8: 'utf-8',
  shiftJis: 'shift-jis',
} as const;

// ファイル出力の固定項目
export const exportFixedFields = {
  recordStartRow: 'レコードの開始行',
  fileName: 'ファイル名',
  uploadedAt: 'アップロード日時',
  updatedAt: '更新日時',
  createdBy: '作成者',
  updatedBy: '更新者',
} as const;

// CSV出力時の引用符の付け方（#3160）
export const exportQuoteMode = {
  // 引用符「なし」。ただし区切り文字・改行・引用符を含む値は CSV が壊れるため囲まれる（既定）
  minimal: 'minimal',
  // 引用符「あり」。すべての値を引用符で囲む
  all: 'all',
} as const;

// 出力する列名（#3158 のカスタマイズ）に許容する最大文字数
export const EXPORT_HEADER_MAX_LENGTH = 50;

// 固定項目IDのプレフィックス（sorted_item_order 内で固定項目を判別する）
export const FIXED_FIELD_ID_PREFIX = 'fixed_';
// 子項目IDの区切り文字（sorted_item_order 内では「親ID-子ID」で子項目を表す）
export const CHILD_FIELD_ID_SEPARATOR = '-';

// 固定項目のIDキーマッピング（sorted_item_orderに使うID -> 表示名）
export const fixedFieldIdMap: Record<string, string> = {
  fixed_recordStartRow: exportFixedFields.recordStartRow,
  fixed_fileName: exportFixedFields.fileName,
  fixed_uploadedAt: exportFixedFields.uploadedAt,
  fixed_updatedAt: exportFixedFields.updatedAt,
  fixed_createdBy: exportFixedFields.createdBy,
  fixed_updatedBy: exportFixedFields.updatedBy,
};

// OCR読み取り信頼度（confidence）の閾値
// Azure: word単位の文字認識の信頼度。この値未満の行を含む項目を「要注意」とする
export const OCR_AZURE_CONFIDENCE_THRESHOLD = 0.5;
// Gemini: レスポンス全体の平均対数確率（avgLogprobs）の閾値。この値未満のレスポンス全項目を「要確認」とする
export const OCR_GEMINI_LOGPROBS_THRESHOLD = -0.5;

// 要注意ロジック（#3262）
// マスタ照合・辞書照合のスコア（0〜100）がこれ未満なら要注意とする。
// マルイチさんの「変換信頼度90%以下だったら要確認」に合わせた値
export const MASTER_MATCH_REVIEW_THRESHOLD = 90;

/**
 * 一覧の要注意フィルタ（#3262）。
 *
 * 「要注意以外」は、一括で確認済にする運用のために用意している。
 * 絞り込んでから全選択し、一括操作の「確認済にする」を使う流れ。
 */
export const needsReviewFilterType = {
  all: 0, // すべて
  only: 1, // 要注意のみ
  exclude: 2, // 要注意以外
} as const;

export const needsReviewFilterLabel: { [key: number]: string } = {
  [needsReviewFilterType.only]: '要注意のみ',
  [needsReviewFilterType.exclude]: '要注意以外',
};

// 照合理由コード（#3309）。
// 要注意の判定はこのコードを最優先で見る（ラベルはマスタの実値が混ざるため）。
// functions/src/types/masterVerificationApi.ts の MatchReason と同じ値。
export const matchReason = {
  confirmed: 'confirmed', // 照合履歴に完全一致（要注意にしない）
  exactMaster: 'exact_master', // マスタと完全一致（要注意にしない）
  similarConfirmed: 'similar_confirmed', // 照合履歴に類似（スコアで判定）
  candidate: 'candidate', // マスタに類似（スコアで判定）
  partialMatch: 'partial_match', // 部分一致（常に要注意）
  noMatch: 'no_match', // 照合したが候補が見つからなかった（常に要注意）
} as const;

// 要注意の判定の出所（#3262）
export const reviewIssueSource = {
  validation: 'validation', // 項目設定のバリデーション制約（必須・文字数・数値範囲など）
  confidence: 'confidence', // OCRの読み取り信頼度（Azure confidence）
  matching: 'matching', // マスタ照合・辞書照合のスコア
} as const;

// 要注意の判定の出所の表示ラベル（#3262）
export const reviewIssueSourceLabel = {
  [reviewIssueSource.validation]: '入力チェック',
  [reviewIssueSource.confidence]: '読み取り信頼度',
  [reviewIssueSource.matching]: 'マスタ照合',
} as const;

/**
 * サマリーのグループ分類（#3262）。
 *
 * source だけだとマスタ照合と辞書変換が同じグループになるため、
 * 表示上はこの 4 分類に分ける。
 */
export const reviewIssueGroup = {
  validation: 'validation',
  matching: 'matching',
  dictionary: 'dictionary',
  confidence: 'confidence',
} as const;

// 見出しは各グループの中身を包含する名前にする（#3339）。
// matching には「マスタ該当なし」「部分一致」、dictionary には「辞書該当なし」も
// 入るため、「一致度 低」だけだと見出しと中身が食い違う。
export const reviewIssueGroupLabel = {
  [reviewIssueGroup.validation]: '入力チェック',
  [reviewIssueGroup.matching]: 'マスタ照合',
  [reviewIssueGroup.dictionary]: '辞書変換',
  [reviewIssueGroup.confidence]: '読み取り信頼度 低',
} as const;

/**
 * サマリーでのグループの並び順・優先順位（#3262）。
 *
 * 1 項目が複数の理由を持つ場合、この順で先に来たグループにだけ出す。
 * 同じ項目名が複数グループに並ぶと件数が多く見えてしまうため。
 * 対処の緊急度が高い順に並べている（入力チェックは明確な不備、
 * 読み取り信頼度は「念のため」の性質が最も弱い）。
 */
export const reviewIssueGroupOrder = [
  reviewIssueGroup.validation,
  reviewIssueGroup.matching,
  reviewIssueGroup.dictionary,
  reviewIssueGroup.confidence,
] as const;

// 要注意の判定コード（#3262）
export const reviewIssueCode = {
  // source: validation
  required: 'REQUIRED', // 必須項目が空
  minLength: 'MIN_LENGTH', // 文字数が下限未満
  maxLength: 'MAX_LENGTH', // 文字数が上限超過
  minValue: 'MIN_VALUE', // 数値が下限未満
  maxValue: 'MAX_VALUE', // 数値が上限超過
  notNumeric: 'NOT_NUMERIC', // 数値型なのに数値として解釈できない
  calculationError: 'CALCULATION_ERROR', // 計算フィールドの計算に失敗
  charType: 'CHAR_TYPE', // 文字種の制約に合わない（#3357）
  pattern: 'PATTERN', // 入力パターン（正規表現）に合わない（#3357）
  dateRange: 'DATE_RANGE', // 日付が許容範囲外（#3357）
  reviewChoice: 'REVIEW_CHOICE', // 要注意に指定された選択肢が選ばれている（#3357）
  // source: confidence
  lowConfidence: 'LOW_CONFIDENCE', // 読み取り信頼度が閾値未満
  // source: matching
  lowMatchScore: 'LOW_MATCH_SCORE', // マスタ照合スコアが閾値未満
  lowDictionaryScore: 'LOW_DICTIONARY_SCORE', // 辞書照合スコアが閾値未満
  partialMatch: 'PARTIAL_MATCH', // 部分一致
  noMatch: 'NO_MATCH', // マスタ照合の候補が見つからなかった
  noDictionaryMatch: 'NO_DICTIONARY_MATCH', // 辞書変換の候補が見つからなかった（#3309）
} as const;

// 要注意のメッセージ生成（#3262）
// 項目名は呼び出し側で付けるため、ここでは理由のみを返す
export const reviewIssueMessage = {
  required: '必須項目です',
  // 明細（複数行データ）が0行のとき（#3309）。「必須項目です」では
  // 明細全体を指していることが伝わらないため文言を分ける
  emptyDetail: '明細が1行もありません',
  minLength: (min: number): string => `${min}文字以上で入力してください`,
  maxLength: (max: number): string => `${max}文字以内で入力してください`,
  minValue: (min: number): string => `${min}以上の値を入力してください`,
  maxValue: (max: number): string => `${max}以下の値を入力してください`,
  // 文字種・入力パターン・日付範囲（#3357）。
  // 具体的な文言は itemValidationResultMessage / dateRangeMessage が作るため、
  // ここは値が取れなかった場合のフォールバックとして持つ
  charType: '入力できない文字が含まれています',
  pattern: '入力形式が正しくありません',
  dateRange: '日付が許容範囲外です',
  // 要注意にする選択肢（#3357）。エラーではなく「人が見た方がよい」ため、
  // 「入力してください」ではなく事実を述べる文言にする
  reviewChoice: (label: string): string => `「${label}」が選ばれています`,
  notNumeric: '数値で入力してください',
  calculationError: '計算に失敗しました',
  // 明細セルの幅が狭いため簡潔にする。「確認してください」は要注意バッジが
  // 既に伝えているため落とし、事実だけを示す
  lowConfidence: '読み取り信頼度 低',
  lowMatchScore: (score: number): string => `マスタ一致度 低（${Math.round(score)}%）`,
  // 辞書変換は別機能なので文言を分ける（#3262）
  lowDictionaryScore: (score: number): string => `辞書一致度 低（${Math.round(score)}%）`,
  partialMatch: 'マスタ部分一致',
  noMatch: 'マスタ該当なし',
  // 辞書変換は別機能なので文言を分ける（#3309）
  noDictionaryMatch: '辞書該当なし',
} as const;

// OCR結果プレビューの全体ズーム設定
export const PREVIEW_ZOOM = {
  MIN_LEVEL: 0.5, // 最小倍率
  MAX_LEVEL: 3.0, // 最大倍率
  STEP: 0.25, // ボタン1クリックごとの変化量
  DEFAULT_LEVEL: 1.0, // 初期倍率
  PERCENT_BASE: 100, // パーセント基数（%表示・CSS幅%・小数2桁丸めに兼用）
  FLOAT_EPSILON: 1e-6, // 倍率比較用の浮動小数点誤差許容値
} as const;

// Webhook イベント種別
export const webhookEventType = {
  resultReviewed: 'result.reviewed',
  resultExported: 'result.exported',
} as const;

export type WebhookEventType = (typeof webhookEventType)[keyof typeof webhookEventType];

// Webhook エンドポイントのステータス
export const webhookEndpointStatus = {
  valid: 1,
  disabled: 99,
} as const;

// Webhook 配信結果ステータス
export const webhookDeliveryStatus = {
  pending: 1,
  success: 2,
  failed: 99,
} as const;

// Webhook 失敗原因
export const webhookFailureReason = {
  timeout: 'timeout',
  dns: 'dns',
  tls: 'tls',
  non2xx: 'non2xx',
  unknown: 'unknown',
} as const;

export type WebhookFailureReason = (typeof webhookFailureReason)[keyof typeof webhookFailureReason];

// Webhook イベント種別の表示ラベル（パターン設定画面用）
export const webhookEventTypeLabel: Record<WebhookEventType, string> = {
  [webhookEventType.resultReviewed]: '確認済（result.reviewed）',
  [webhookEventType.resultExported]: 'ファイル出力済（result.exported）',
};

// Webhook 配信結果ステータスの表示ラベル
export const webhookDeliveryStatusLabel: Record<number, string> = {
  [webhookDeliveryStatus.pending]: '配信待ち',
  [webhookDeliveryStatus.success]: '成功',
  [webhookDeliveryStatus.failed]: '失敗',
};

// OCR結果プレビューのルーペ（マウスオーバー拡大）設定
export const PREVIEW_MAGNIFIER = {
  WIDTH: 600, // ルーペの横幅（px）
  HEIGHT: 320, // ルーペの縦幅（px）
  SCALE: 2.0, // ルーペ内の拡大倍率
  BORDER_WIDTH: 2, // 枠線の太さ（px）
  Z_INDEX: 1000, // 他要素より手前に表示
} as const;

// APIキーのステータス
export const apiKeyStatus = {
  valid: 1, // 有効
  rotating: 2, // 再生成中（旧キーが grace 期間で生きている）
  revoked: 99, // 無効化
} as const;

// APIキーのステータスラベル
export const apiKeyStatusLabel: Record<number, string> = {
  [apiKeyStatus.valid]: '有効',
  [apiKeyStatus.rotating]: '再生成中',
  [apiKeyStatus.revoked]: '無効化',
};

// APIキーの環境種別
export const apiKeyEnvironment = {
  live: 'live',
  test: 'test',
} as const;

// APIキー再生成の猶予期間（分）
export const apiKeyRotateGracePeriod = {
  immediate: 0,
  oneHour: 60,
  twentyFourHours: 24 * 60,
} as const;

// APIキー再生成の猶予期間ラベル
export const apiKeyRotateGracePeriodLabel: Record<number, string> = {
  [apiKeyRotateGracePeriod.immediate]: '即時失効',
  [apiKeyRotateGracePeriod.oneHour]: '1 時間後に失効',
  [apiKeyRotateGracePeriod.twentyFourHours]: '24 時間後に失効',
};

// API エラーコード（バックエンドと共有）
export const apiErrorCode = {
  INVALID_API_KEY: 'INVALID_API_KEY',
  IP_NOT_ALLOWED: 'IP_NOT_ALLOWED',
  API_FEATURE_DISABLED: 'API_FEATURE_DISABLED',
  PLAN_NOT_ALLOWED: 'PLAN_NOT_ALLOWED',
  INVALID_REQUEST: 'INVALID_REQUEST',
  UNSUPPORTED_FILE_TYPE: 'UNSUPPORTED_FILE_TYPE',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  CREDIT_INSUFFICIENT: 'CREDIT_INSUFFICIENT',
  IDEMPOTENT_CONFLICT: 'IDEMPOTENT_CONFLICT',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const;

// API キー上限（1 企業あたり）
export const API_KEY_MAX_PER_COMPANY = 30;

// API キーごとの IP ホワイトリスト上限（CIDR 件数）
export const API_IP_ALLOWLIST_MAX = 50;

// API キーのプレフィックス（バックエンド constants/api.ts と同期）
export const apiKeyPrefix = {
  live: 'gd_live_',
  test: 'gd_test_',
} as const;

// API リクエストの上限値（バックエンド functions/src/constants/api.ts と二重管理。変更時は両方を揃える）
export const apiRequestLimits = {
  /** 1 リクエストあたりに含められるファイルの最大数 */
  filesPerRequest: 30,
  /** 1 ファイルあたりの最大サイズ（Base64 デコード後のバイト数） */
  fileBytesMax: 7 * 1024 * 1024,
  /**
   * Cloud Run / Functions v2 の HTTP リクエストボディ上限（32 MB）から
   * Base64 エンコードのオーバーヘッド (3/4) を逆算したデコード後の実効上限。
   */
  totalBytesMax: 24 * 1024 * 1024,
} as const;

// 環境別 Firebase プロジェクト ID（API ホスト推定用）
export const firebaseProjectIds = {
  prd: 'gendrive-958b4',
  stg: 'gen-drive-stg',
} as const;

// 環境別 API ホスト
// dev (local) はカスタムドメイン未設定のため Cloud Functions の直 URL を使用
export const apiHosts = {
  prd: 'https://api.gen-drive.com',
  stg: 'https://api-stg.gen-drive.com',
  local: 'https://us-central1-gen-drive-dev-1f03a.cloudfunctions.net/ocrApi',
} as const;

// 辞書機能（fuse.js あいまい照合）の上限・しきい値
// 件数が多いとパフォーマンスが下がるため 1 辞書あたりの行数を制限する
export const DICTIONARY_MAX_ROWS = 200;
// fuse.js のあいまい検索しきい値（0 に近いほど厳密、1 に近いほど緩い）
export const DICTIONARY_FUSE_THRESHOLD = 0.4;

// =============================================================
// セキュリティアドオン（#2982 IP制限 / #2983 SAML / #2984 MFA / #2985 監査ログ）
// 設計書: documents/develop/issues/2982-security/security-features-design.md（v0.2）
// Firestore は snake_case。値は functions/src/constants/enum.ts と二重管理（変更時は両方揃える）
// =============================================================

// テナント単位の IP 許可リスト上限（CIDR 件数 / テナント）。APIキー単位の上限 API_IP_ALLOWLIST_MAX(50) とは別枠
export const SECURITY_IP_ALLOWLIST_MAX = 100;

// APIキーの IP 制限モード（api_keys/{keyId}.ip_mode）
export const apiKeyIpMode = {
  inherit: 'inherit', // 画面用（テナント）許可リストを継承
  custom: 'custom', // キー専用の許可リストを使用
  none: 'none', // IP 制限なし
} as const;

export type ApiKeyIpMode = (typeof apiKeyIpMode)[keyof typeof apiKeyIpMode];

// APIキー IP 制限モードの表示ラベル
export const apiKeyIpModeLabel: Record<ApiKeyIpMode, string> = {
  [apiKeyIpMode.inherit]: '画面の許可リストを継承',
  [apiKeyIpMode.custom]: '専用の許可リストを指定',
  [apiKeyIpMode.none]: 'IP 制限なし',
};

// SAML の IdP 種別（security_settings/config.saml.idp）
export const samlIdp = {
  entra: 'entra', // Microsoft Entra ID（旧 Azure AD）
  google: 'google', // Google Workspace
} as const;

export type SamlIdp = (typeof samlIdp)[keyof typeof samlIdp];

// SAML IdP の表示ラベル
export const samlIdpLabel: Record<SamlIdp, string> = {
  [samlIdp.entra]: 'Microsoft Entra ID',
  [samlIdp.google]: 'Google Workspace',
};

// SAML ログインモード（security_settings/config.saml.mode）
export const samlMode = {
  required: 'required', // SAML 必須
  optional: 'optional', // 従来ログイン併用可
} as const;

export type SamlMode = (typeof samlMode)[keyof typeof samlMode];

// SAML ログインモードの表示ラベル
export const samlModeLabel: Record<SamlMode, string> = {
  [samlMode.required]: 'SAML 必須',
  [samlMode.optional]: '従来ログイン併用可',
};

// SAML SSO（#2983）の定数（functions/src/constants/enum.ts と二重管理）
// SAML 設定入力値の上限（過大ペイロード防止）
export const SAML_DOMAIN_MAX_LENGTH = 255;
export const SAML_ENTITY_ID_MAX_LENGTH = 1024;
export const SAML_SSO_URL_MAX_LENGTH = 2048;
export const SAML_CERT_MAX_LENGTH = 10000;
// SAML セッションの有効時間（IdP 側デプロビジョニングの遮断緩和策・PRD §10 R3）
export const SAML_SESSION_TTL_HOURS = 8;
// ログイン画面でメール入力から SSO プロバイダ解決を先行実行するまでの待ち時間
export const SAML_SSO_PREFETCH_DEBOUNCE_MS = 500;
// GCIP の SAML プロバイダ ID の共通プレフィックス（例: saml.gd-<companyId>）。
// Gen Drive が作成するプロバイダは functions 側の SAML_PROVIDER_ID_PREFIX（'saml.gd-'）だが、
// 「SAML でログイン / 登録されたか」の判定は GCIP 共通の 'saml.' 前方一致で行う（#3214）
export const SAML_PROVIDER_ID_PREFIX = 'saml.';
// SSO（IdP）で再認証する操作のボタンラベル（MFA の設定・解除ダイアログで共通利用・#3214）
export const SSO_REAUTH_LABEL = 'SSOで再認証';

// 監査ログ 操作種別（audit_logs/{logId}.action）
export const auditAction = {
  login: 'login', // ログイン
  logout: 'logout', // ログアウト
  loginFailed: 'login_failed', // ログイン失敗
  create: 'create', // 作成
  update: 'update', // 変更
  delete: 'delete', // 削除
  execute: 'execute', // 実行（OCR 等）
  download: 'download', // ダウンロード（ファイルDL / 監査ログエクスポート）
  invite: 'invite', // 招待（ユーザー招待）
  revoke: 'revoke', // 失効（APIキー失効）
} as const;

export type AuditAction = (typeof auditAction)[keyof typeof auditAction];

// 監査ログ 操作種別の表示ラベル（閲覧UIのフィルタ・一覧用）
export const auditActionLabel: Record<AuditAction, string> = {
  [auditAction.login]: 'ログイン',
  [auditAction.logout]: 'ログアウト',
  [auditAction.loginFailed]: 'ログイン失敗',
  [auditAction.create]: '作成',
  [auditAction.update]: '変更',
  [auditAction.delete]: '削除',
  [auditAction.execute]: '実行',
  [auditAction.download]: 'ダウンロード',
  [auditAction.invite]: '招待',
  [auditAction.revoke]: '失効',
};

// 監査ログ 対象リソース種別（audit_logs/{logId}.resource_type）
export const auditResourceType = {
  session: 'session', // ログイン/ログアウト
  readPattern: 'read_pattern', // 読み取りパターン
  ocrJob: 'ocr_job', // OCR 実行
  file: 'file', // ファイル
  user: 'user', // ユーザー
  role: 'role', // 権限
  ipRestriction: 'ip_restriction', // IP 制限設定
  samlSetting: 'saml_setting', // SAML 設定
  mfaSetting: 'mfa_setting', // MFA 設定（テナント強制含む）
  mfaBackupCode: 'mfa_backup_code', // MFA バックアップコード（使用・再発行）
  apiKey: 'api_key', // API キー発行・失効
  auditLog: 'audit_log', // 監査ログ閲覧/エクスポート
} as const;

export type AuditResourceType = (typeof auditResourceType)[keyof typeof auditResourceType];

// 監査ログ 対象リソース種別の表示ラベル
export const auditResourceTypeLabel: Record<AuditResourceType, string> = {
  [auditResourceType.session]: 'セッション',
  [auditResourceType.readPattern]: '読み取りパターン',
  [auditResourceType.ocrJob]: 'OCR 実行',
  [auditResourceType.file]: 'ファイル',
  [auditResourceType.user]: 'ユーザー',
  [auditResourceType.role]: '権限',
  [auditResourceType.ipRestriction]: 'IP 制限設定',
  [auditResourceType.samlSetting]: 'SAML 設定',
  [auditResourceType.mfaSetting]: 'MFA 設定',
  [auditResourceType.mfaBackupCode]: 'バックアップコード',
  [auditResourceType.apiKey]: 'API キー',
  [auditResourceType.auditLog]: '監査ログ',
};

// 監査ログ 操作名のキー（action と resource_type の組を一意に表す）
export const auditOperationKey = (action: string, resourceType: string): string =>
  `${action}:${resourceType}`;

// 監査ログ 操作名の表示ラベル（一覧・フィルタ・CSV 用）
// アクション（作成/変更 等）と対象（読み取りパターン 等）を分離表示すると
// 直感的に読めないため、「読み取りパターン作成」「OCR実行」のような
// 操作単位の結合ラベルで表示する。記録される組み合わせのみ定義する
export const auditOperationLabel: Record<string, string> = {
  [auditOperationKey(auditAction.login, auditResourceType.session)]: 'ログイン',
  [auditOperationKey(auditAction.logout, auditResourceType.session)]: 'ログアウト',
  [auditOperationKey(auditAction.loginFailed, auditResourceType.session)]: 'ログイン失敗',
  [auditOperationKey(auditAction.create, auditResourceType.readPattern)]: '読み取りパターン作成',
  [auditOperationKey(auditAction.update, auditResourceType.readPattern)]: '読み取りパターン変更',
  [auditOperationKey(auditAction.delete, auditResourceType.readPattern)]: '読み取りパターン削除',
  [auditOperationKey(auditAction.execute, auditResourceType.ocrJob)]: 'OCR実行',
  [auditOperationKey(auditAction.download, auditResourceType.file)]: 'ファイルダウンロード',
  [auditOperationKey(auditAction.invite, auditResourceType.user)]: 'ユーザー招待',
  [auditOperationKey(auditAction.delete, auditResourceType.user)]: 'ユーザー削除',
  [auditOperationKey(auditAction.update, auditResourceType.role)]: '権限変更',
  [auditOperationKey(auditAction.execute, auditResourceType.mfaBackupCode)]:
    'バックアップコードでログイン',
  [auditOperationKey(auditAction.create, auditResourceType.mfaBackupCode)]:
    'バックアップコード発行',
  [auditOperationKey(auditAction.create, auditResourceType.apiKey)]: 'APIキー作成',
  [auditOperationKey(auditAction.revoke, auditResourceType.apiKey)]: 'APIキー失効',
  [auditOperationKey(auditAction.download, auditResourceType.auditLog)]: '監査ログエクスポート',
};

// 操作名ラベルの解決。未定義の組み合わせは「対象＋アクション」で合成する
export const auditOperationLabelFor = (action: string, resourceType: string): string => {
  const known = auditOperationLabel[auditOperationKey(action, resourceType)];
  if (known) return known;
  const actionLabel = auditActionLabel[action as AuditAction] ?? action;
  const resourceLabel = auditResourceTypeLabel[resourceType as AuditResourceType] ?? resourceType;
  return `${resourceLabel}${actionLabel}`;
};

// 監査ログ 実行結果（audit_logs/{logId}.result）
export const auditResult = {
  success: 'success', // 成功
  failure: 'failure', // 失敗
} as const;

export type AuditResult = (typeof auditResult)[keyof typeof auditResult];

// 監査ログ 実行結果の表示ラベル
export const auditResultLabel: Record<AuditResult, string> = {
  [auditResult.success]: '成功',
  [auditResult.failure]: '失敗',
};

// 監査ログ CSV エクスポートの上限件数（1 回あたり。超過は期間分割を促す）
export const AUDIT_LOG_EXPORT_MAX = 100000;

// 監査ログ CSV エクスポートの 1 コール返却上限
// （callable の応答サイズ上限 約10MB を超えないよう分割取得する）
export const AUDIT_LOG_EXPORT_BATCH_SIZE = 1000;

// 監査ログ閲覧の 1 ページ表示件数
export const AUDIT_LOG_PAGE_SIZE = 100;

// ホット層（画面から即時検索できる範囲）の保持月数（PRD §5-4）
export const AUDIT_HOT_RETENTION_MONTHS = 3;

// 監査ログ全体の標準保存月数（ホット3ヶ月＋コールド4〜13ヶ月目・PRD §5-4）
export const AUDIT_TOTAL_RETENTION_MONTHS = 13;

// コールド層エクスポートのダウンロード URL 有効時間（PRD §5-4: TTL 24h）
export const AUDIT_COLD_EXPORT_URL_TTL_HOURS = 24;

// ログアウト時に監査ログ記録の完了を待つ上限時間（#3110）
// callable のコールドスタートでログアウトが待たされ続けるのを防ぐ。
// 超過してもリクエスト自体は継続するため、通常は記録される。
export const LOGOUT_AUDIT_LOG_TIMEOUT_MS = 3 * 1000;

// MFA バックアップコード（8 桁 × 10 個）
export const MFA_BACKUP_CODE_COUNT = 10;
export const MFA_BACKUP_CODE_LENGTH = 8;

// バックアップコードの残数がこの数以下になったら再発行を促す（#3123）
export const MFA_BACKUP_CODE_LOW_REMAINING = 3;

// MFA 強制ゲートの再判定間隔の下限（タブ復帰のたびの callable 過多を防ぐ）
export const MFA_REQUIREMENT_RECHECK_MIN_INTERVAL_MS = 60 * 1000;

// 認証アプリ（TOTP）の確認コード桁数（#2984）
export const MFA_TOTP_CODE_LENGTH = 6;

// MFA ログインチャレンジのセッション（第一要素の証明）が期限切れ・無効になった際の
// Firebase Auth エラーコード（#3105）。再ログインでしか解消できないため専用の案内を出す。
// auth/totp-challenge-timeout は dev で実際に返ることを確認済み
// （"TOTP challenge timeout, provide first factor again."）。
// 残り 2 つは同じく第一要素の証明が無効な場合のコードで、同じ案内が妥当なため含める
export const MFA_SESSION_EXPIRED_AUTH_ERROR_CODES = [
  'auth/totp-challenge-timeout',
  'auth/invalid-multi-factor-session',
  'auth/missing-multi-factor-session',
] as const;

// Firebase Auth のメールアクション種別（URL の mode パラメータ・#3300）。
// Firebase が送る通知メールのリンクに付与され、/action の遷移先分岐に使う
export const firebaseActionMode = {
  resetPassword: 'resetPassword',
  verifyEmail: 'verifyEmail',
  recoverEmail: 'recoverEmail',
  revertSecondFactorAddition: 'revertSecondFactorAddition',
} as const;

// 設定 > セキュリティ 画面の内部タブ（#2982-2985）
export const securitySettingsTab = {
  ipRestriction: 'ip_restriction', // IP 制限（#2982）
  saml: 'saml', // SAML SSO（#2983）
  mfa: 'mfa', // MFA 強制（#2984）
  auditLog: 'audit_log', // 監査ログ（#2985）
} as const;

export type SecuritySettingsTab = (typeof securitySettingsTab)[keyof typeof securitySettingsTab];

// セキュリティ設定タブの表示ラベル
export const securitySettingsTabLabel: Record<SecuritySettingsTab, string> = {
  [securitySettingsTab.ipRestriction]: 'IP アドレス制限',
  [securitySettingsTab.saml]: 'SAML SSO',
  [securitySettingsTab.mfa]: 'MFA（多要素認証）',
  [securitySettingsTab.auditLog]: '監査ログ',
};

// ===== メール取り込み（#2965）=====

// 専用受信アドレスの種別
export const emailInboxType = {
  companyCommon: 'company_common', // 会社共通（受信後に自動仕分け）
  pattern: 'pattern', // 読取パターン固有（紐づくパターンを直接適用）
} as const;
export type EmailInboxType = (typeof emailInboxType)[keyof typeof emailInboxType];

// 受信メールの処理ステータス
export const emailMessageStatus = {
  received: 'received', // 受信済み
  processing: 'processing', // OCR連携中
  completed: 'completed', // 取り込み完了
  rejected: 'rejected', // 取り込み対象外（allowlist外・認証NG等）
  failed: 'failed', // 処理失敗
  bounced: 'bounced', // 送信側バウンス
} as const;
export type EmailMessageStatus = (typeof emailMessageStatus)[keyof typeof emailMessageStatus];

// 取り込まなかった理由（rejected の内訳）
export const emailRejectReason = {
  notAllowlisted: 'not_allowlisted', // allowlist外の送信元
  authFailed: 'auth_failed', // SPF/DKIM/DMARC 検証失敗
  unsupportedExtension: 'unsupported_extension', // 非対応拡張子
  tooLarge: 'too_large', // サイズ上限超過
  tooManyAttachments: 'too_many_attachments', // 添付数上限超過
  tooManyPages: 'too_many_pages', // 合計ページ数上限超過
  pdfTooManyPages: 'pdf_too_many_pages', // 1PDFあたりのページ数上限超過
  noAttachment: 'no_attachment', // 添付なし
  inboxDisabled: 'inbox_disabled', // 受信アドレス無効
  inboxNotFound: 'inbox_not_found', // 宛先アドレス未登録
} as const;
export type EmailRejectReason = (typeof emailRejectReason)[keyof typeof emailRejectReason];

// ===== OCR一覧のファイル名列幅（#3134）=====

// ファイル名列は余った横幅を吸収して広がるため、ここで定義するのは「確保する最小幅」。
// 一覧に表示するカスタマイズ列（OCR読取項目）が増えるほど最小幅を下げ、
// 横幅が足りなくなったときに他の列へ幅を譲れるようにする。
// これ以上狭めるとファイル名がほとんど読めなくなるため、下限は narrow で打ち止めにする。
export const fileNameColumnWidth = {
  wide: 280, // カスタマイズ列が少ない場合（従来どおりの幅）
  narrow: 240, // カスタマイズ列が多い場合の下限
} as const;

// ファイル名列の最小幅を切り替えるカスタマイズ列数の境界値
export const fileNameColumnWidthThreshold = {
  narrow: 3, // この列数以上で narrow まで最小幅を下げる
} as const;

// テーブルセルの左右パディング合計（セル内で使える実効幅の算出に使う）
export const tableCellHorizontalPaddingTotal = 32;

// カスタマイズ表示列（OCR読取項目）の列幅
export const ocrColumnWidth = 160;

// フィールドコードの最大文字数（#3175）
// 計算フィールド・指定フォーマット出力の識別子として使うため、長すぎる値を弾く。
export const FIELD_CODE_MAX_LENGTH = 50;

// フィールドコードの重複時に付ける連番の上限（#3175）
// 自動採番で衝突が続く場合の打ち切り。これを超えたら項目IDベースのコードにフォールバックする。
export const FIELD_CODE_SUFFIX_MAX = 999;

// ===== 計算フィールド（#3145）=====

// 上限値・関数名・エラーコードの定義は計算エンジン（utils/calculation/constants.ts）が正。
// エンジンは functions/ と main/ の二重実装で外部依存を持てないため、
// enum.ts からは再エクスポートして既存の参照慣習（constants/enum.ts 経由）に合わせる。
// フィールドコード関連の定数（FIELD_CODE_MAX_LENGTH 等）は #3175 のものを上で定義している。
export {
  CALCULATION_FORMULA_MAX_LENGTH,
  CALCULATION_REGEX_MAX_LENGTH,
  CALCULATION_TEXT_MAX_LENGTH,
  CALCULATION_MAX_DEPENDENCY_DEPTH,
  CALCULATION_FIELD_MAX_COUNT,
  calculationFunction,
  calculationFunctionArity,
  calculationErrorCode,
  calculationWarningCode,
  calculationErrorMessage,
  calculationWarningMessage,
} from '../utils/calculation/constants';
export type {
  CalculationFunctionName,
  CalculationErrorCode,
  CalculationWarningCode,
} from '../utils/calculation/constants';

// 計算フィールドの内訳で SUM を「100 + 200 + 300」と展開する明細行数の上限（#3412）
// 行数が多いとセル下のグレー文言が何行にも折り返して読めなくなるため、超えたら合計と行数だけを出す。
export const CALCULATION_BREAKDOWN_SUM_EXPAND_MAX_ROWS = 5;

// 選択肢（ラジオボタン・プルダウン）の最大件数（#3075）
// 都道府県（47件）が収まる件数にしている。部門・支店の実用範囲もおおむねカバーできる。
// 候補はすべてOCRプロンプトに載るが、47件でも約250トークンでプロンプト全体から見れば誤差の範囲。
// これを超える候補数（商品マスタ・勘定科目など数百件）はマスタ照合機能の領域とする。
// 人が目で見て選べる量を超えると、選ぶ側もAI側も精度が落ちるため。
export const CHOICE_MAX_COUNT = 50;

// 指定フォーマット出力（#3070）
// 出力モード。plain は従来の単純出力、template は書式付きフォーマットへの流し込み。
export const exportMode = {
  plain: 'plain',
  template: 'template',
} as const;

// フォーマット名の最大文字数
export const EXPORT_TEMPLATE_NAME_MAX_LENGTH = 100;

// アップロードできるフォーマットの最大サイズ（10MB）
// 京阪産業のサンプル11ファイルは最大132KBのため十分な余裕がある。
export const EXPORT_TEMPLATE_MAX_FILE_SIZE = 10 * 1024 * 1024;

// 1パターンあたりに登録できるフォーマット数の上限
export const EXPORT_TEMPLATE_MAX_COUNT_PER_PATTERN = 20;

// 子パターン（#2967）
// 子パターンの名称の最大文字数
export const SUB_PATTERN_NAME_MAX_LENGTH = 50;

// 子パターンの説明文の最大文字数。BE（functions/src/constants/enum.ts）と一致させること。
// この文章がそのまま仕分け判定のプロンプトに載るため、長すぎるとプロンプトが肥大する。
export const SUB_PATTERN_DESCRIPTION_MAX_LENGTH = 500;

// 子パターンを適用しない「標準」を表すID。子パターン判定の戻り値と再読み取りの指定に使う。
// 実在するドキュメントIDではないため、Firestore の参照に使ってはいけない。
export const SUB_PATTERN_ID_BASE = 'base';

// 再読み取りで「もう一度判定させる」を表す値。子パターンIDの代わりに指定する。
// BE（reOcrPatternHandler）と同じ文字列にすること。
export const SUB_PATTERN_ID_AUTO = 'auto';

// アップロード経路（#2967）
// 結果がどの画面から作られたかを記録する。仕分けコストの分析と、
// 「親の自動仕分けを経たか」の切り分けに使う。
export const uploadSource = {
  classification: 'classification', // ホームからの自動仕分け
  pattern: 'pattern', // パターン別画面
  api: 'api', // 外部API
  email: 'email', // メール取込
} as const;

export type UploadSource = (typeof uploadSource)[keyof typeof uploadSource];

// 重なり順（z-index）の一覧（#3224）
// 値を個別のコンポーネントに散らすと相対関係がコメントでしか担保されないため、
// 画面をまたぐレイヤーはここに集約する。MUI の既定（Drawer 1200 / Modal 1300）に揃えており、
// MUI Dialog（1300）はサイドバー（1200）より上になるためモーダルが隠れることはない。
export const zIndexLayer = {
  stickyDetailTableHeader: 2, // OCR結果画面の明細テーブルのヘッダー行（#3356。テーブル内で行の上に重ねる）
  stickyPageHeaderSp: 9, // OCR結果画面のヘッダー（SP。SPヘッダーの下に貼り付く）
  stickyPageHeader: 10, // OCR結果画面のヘッダー（PC）
  spHeader: 10, // SPヘッダー（メニューを閉じているとき）
  fixedFooter: 11, // 画面下部の固定ボタン
  spHeaderBehindMenu: 1198, // SPヘッダー（メニュー展開中。Backdrop より下げて暗転させる）
  backdrop: 1199, // メニュー展開時の Backdrop
  sideBar: 1200, // サイドバー本体
  globalHeader: 1299, // SP のグローバルヘッダー（2段目）
  sideBarWrapper: 1300, // 共通レイアウトのサイドバー枠（グローバルヘッダーより上）
  proxyBanner: 1400, // 代理ログイン中のバナー
  loading: 2000, // CustomLoading（常に最前面）
};

// 明細テーブルのヘッダー行固定の条件（#3356）
// 行数が多いとスクロール中にどの列か分からなくなるため、
// 行数がしきい値を超えたテーブルだけテーブル自身を縦スクロールできるようにし、
// ヘッダー行を貼り付ける。数行しかない明細まで対象にすると、
// ページスクロールと二重になって操作しにくい。
// 高さは固定値ではなく画面の高さから算出する。明細が1つだけの帳票では
// 画面の下まで使えるため、固定値にすると表示できる行数を損ねる。
// 対象は PC（960px 以上）のみ。SP はページ全体がスクロールするため、
// ここで枠を作ると狭い画面に入れ子スクロールができて操作しにくい。
export const detailTableStickyHeader = {
  // この行数を超えたら（11行以上で）ヘッダー行の固定を有効にする。
  // 有効にしても高さは maxHeight までしか取らないため、
  // 画面に収まる行数なら枠は中身の高さまでしか伸びずスクロールも出ない。
  // 実際にスクロールするのは行が maxHeight を超えたときだけ
  minRowCount: 10,
  reservedVh: 32, // 画面上部のヘッダーと明細の前後に残す余白（ビューポート高に対する％）
};

// 消費枚数の計算条件（#3315）
// 運営向けコスト内訳の「粗利」欄で消費枚数の内訳を示すために使う。
// BE（functions/src/utils/calculateUsageCount）の計算と一致させること。
export const usageCountRule = {
  fieldsPerUnit: 300, // 入力欄がこの個数ごとに +1枚
  charsPerUnit: 5000, // 文字数がこの文字数ごとに +1枚
  defaultPricePerUnitJpy: 15, // 1枚あたりの既定単価（円）
  minGrossProfitJpy: 5, // 粗利がこの金額を下回るときは粗利補正で +1枚
};

// 項目（パターンの item_list）がマスタ照合・辞書変換の設定から参照されている箇所の種別（#3344）
// 項目削除時に、参照箇所の役割によって「削除をブロックする」か「自動で解除する」かを分ける。
// 照合キー（searchKey 系）はマッピングの成立条件そのものなのでブロックし、
// それ以外は欠けても照合が成立するため警告のうえ自動解除する。
export const itemReferenceKind = {
  masterSearchKey: 'masterSearchKey', // マスタ照合キー（削除ブロック）
  dictionarySearchKey: 'dictionarySearchKey', // 辞書の照合元項目（削除ブロック）
  masterFilterKey: 'masterFilterKey', // マスタ絞り込みキー（自動解除）
  masterColumnMapping: 'masterColumnMapping', // マスタ反映先（自動解除）
  dictionaryColumnMapping: 'dictionaryColumnMapping', // 辞書反映先（自動解除）
  masterScopeKey: 'masterScopeKey', // 履歴分離キー（自動解除）
} as const;
